import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { chequesApi } from '../../utils/chequesApi';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './CashBanks.css';

const fmt = (val) =>
  val != null
    ? Number(val).toLocaleString('en-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

export default function ChequesPrintPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cheque, setCheque] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    chequesApi.get(id)
      .then(setCheque)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div style={{ padding: 40 }}><LoadingSpinner variant="default" /></div>;
  if (error) return (
    <div style={{ padding: 40, color: '#991b1b' }}>
      <i className="fas fa-exclamation-circle" style={{ marginRight: 8 }} />{error}
      <button className="cb-btn cb-btn--secondary" style={{ marginLeft: 16 }} onClick={() => navigate(-1)}>Back</button>
    </div>
  );
  if (!cheque) return null;

  const bankName  = cheque.book?.bankName || cheque.account?.name || 'Bank';
  const currency  = cheque.account?.currency?.code || 'EGP';

  return (
    <div style={{ padding: '24px', maxWidth: 900, margin: '0 auto' }}>
      {/* Toolbar — hidden on print */}
      <div className="cb-print-hide" style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'center' }}>
        <button className="cb-btn cb-btn--secondary" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left" /> Back
        </button>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 600, flex: 1 }}>
          Cheque Print Preview — #{cheque.chequeNumber}
        </h1>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Status: <strong>{cheque.status}</strong>
        </span>
        {cheque.status === 'VOID' && (
          <span style={{ background: '#fee2e2', color: '#991b1b', padding: '4px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600 }}>
            VOID — Do not honour
          </span>
        )}
        <button className="cb-btn cb-btn--primary" onClick={() => window.print()}>
          <i className="fas fa-print" /> Print
        </button>
      </div>

      {/* Cheque Template */}
      <div className="cb-cheque-template">
        {/* VOID watermark */}
        {cheque.status === 'VOID' && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
            justifyContent: 'center', pointerEvents: 'none', zIndex: 10,
          }}>
            <span style={{
              fontSize: 80, fontWeight: 900, color: 'rgba(220,38,38,0.18)',
              transform: 'rotate(-30deg)', letterSpacing: 8, userSelect: 'none',
            }}>VOID</span>
          </div>
        )}

        {/* Header */}
        <div className="cb-cheque-header">
          <div>
            <div className="cb-cheque-bank">{bankName}</div>
            {cheque.book?.bookCode && (
              <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Book: {cheque.book.bookCode}</div>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="cb-cheque-num">No. <strong>{cheque.chequeNumber}</strong></div>
            <div style={{ fontSize: 11, color: '#888' }}>El Alsson Schools</div>
          </div>
        </div>

        {/* Date */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
          <span style={{ fontSize: 12, color: '#555', marginRight: 8 }}>Date:</span>
          <span style={{ fontSize: 13, fontWeight: 600, borderBottom: '1px solid #000', minWidth: 120, textAlign: 'center' }}>
            {cheque.issueDate?.slice(0, 10)}
          </span>
        </div>

        {/* Pay to */}
        <div className="cb-cheque-payee-row">
          <span className="cb-cheque-payee-label">Pay to the order of:</span>
          <span className="cb-cheque-payee-line">&nbsp;{cheque.payee}</span>
        </div>

        {/* Amount */}
        <div className="cb-cheque-amount-row">
          <div className="cb-cheque-words-box">
            {cheque.amountWords || <span style={{ color: '#bbb', fontStyle: 'italic' }}>Amount in words not provided</span>}
          </div>
          <div className="cb-cheque-amount-box">
            {currency}&nbsp;{fmt(cheque.amount)}
          </div>
        </div>

        {/* Footer */}
        <div className="cb-cheque-footer">
          <div>
            {cheque.notes && (
              <div style={{ fontSize: 11, color: '#666' }}>
                <span style={{ fontWeight: 600 }}>Memo:</span> {cheque.notes}
              </div>
            )}
            {cheque.voucher && (
              <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                Ref: {cheque.voucher.type}-{cheque.voucher.voucherNumber}
              </div>
            )}
          </div>
          <div className="cb-cheque-sig">Authorised Signature</div>
        </div>
      </div>

      {/* Detail panel — hidden on print */}
      <div className="cb-print-hide" style={{ marginTop: 24 }}>
        <div className="cb-card">
          <div className="cb-card-header">
            <h3 className="cb-card-title">Cheque Details</h3>
          </div>
          <div style={{ padding: '16px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 14 }}>
            {[
              ['Cheque #',    cheque.chequeNumber],
              ['Bank Account', `${cheque.account?.code} — ${cheque.account?.name}`],
              ['Book Code',   cheque.book?.bookCode],
              ['Payee',       cheque.payee],
              ['Amount',      `${fmt(cheque.amount)} ${currency}`],
              ['Issue Date',  cheque.issueDate?.slice(0, 10)],
              ['Status',      cheque.status],
              ['Linked PV',   cheque.voucher ? `${cheque.voucher.type}-${cheque.voucher.voucherNumber}` : '—'],
              ['Issued by',   cheque.createdBy?.fullName],
              ['Cleared at',  cheque.clearedAt ? cheque.clearedAt.slice(0, 10) : '—'],
            ].map(([label, value]) => (
              <div key={label}>
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{label}</span>
                <div style={{ fontWeight: 500, marginTop: 2 }}>{value ?? '—'}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
